'use client'

import { useEffect, useState } from 'react'
import {
  hasConsentDecision,
  grantConsent,
  denyConsent,
  getRegionPolicy,
  type RegionPolicy,
} from '@/lib/consent-manager'
import { recordConsentMetric } from '@/lib/consent-metrics'

/**
 * Policy-aware banner copy. Two variants, same visual design:
 * - prior-consent (EEA/UK/CH or unknown region): the original opt-in ask —
 *   nothing runs until Accept.
 * - notice-opt-out (rest of world): analytics runs by default, so the copy
 *   must say so truthfully and offer an opt-out — no "Accept"/"Reject"
 *   framing for a decision that is actually notice + opt-out.
 */
type ConsentLocale = 'en' | 'ar'

type ConsentCopy = {
  intro: string
  message: string
  learnMore: string
  dialogLabel: string
  primaryLabel: string
  primaryAria: string
  secondaryLabel: string
  secondaryAria: string
}

const BANNER_COPY: Record<ConsentLocale, Record<RegionPolicy, ConsentCopy>> = {
  en: {
    'prior-consent': {
      intro: 'We value your privacy.',
      message:
        'We use cookies and similar technologies to analyze site usage and improve your experience. You can choose to accept or decline analytics tracking.',
      learnMore: 'Learn more',
      dialogLabel: 'Cookie consent banner',
      primaryLabel: 'Accept All',
      primaryAria: 'Accept all analytics cookies',
      secondaryLabel: 'Reject All',
      secondaryAria: 'Reject all analytics cookies',
    },
    'notice-opt-out': {
      intro: 'We value your privacy.',
      message:
        'We use analytics cookies to understand site usage and improve your experience. Analytics is active by default in your region — you can opt out at any time.',
      learnMore: 'Learn more',
      dialogLabel: 'Cookie consent banner',
      primaryLabel: 'Got It',
      primaryAria: 'Acknowledge analytics notice',
      secondaryLabel: 'Opt Out',
      secondaryAria: 'Opt out of analytics cookies',
    },
  },
  ar: {
    'prior-consent': {
      intro: 'نحترم خصوصيتك.',
      message:
        'نستخدم ملفات تعريف الارتباط وتقنيات مشابهة لتحليل استخدام الموقع وتحسين تجربتك. يمكنك قبول تتبع التحليلات أو رفضه.',
      learnMore: 'اعرف المزيد',
      dialogLabel: 'إعدادات ملفات تعريف الارتباط',
      primaryLabel: 'قبول الكل',
      primaryAria: 'قبول جميع ملفات تعريف الارتباط التحليلية',
      secondaryLabel: 'رفض الكل',
      secondaryAria: 'رفض جميع ملفات تعريف الارتباط التحليلية',
    },
    'notice-opt-out': {
      intro: 'نحترم خصوصيتك.',
      message:
        'نستخدم ملفات تعريف الارتباط التحليلية لفهم استخدام الموقع وتحسين تجربتك. التحليلات مفعّلة افتراضيًا في منطقتك، ويمكنك إيقافها في أي وقت.',
      learnMore: 'اعرف المزيد',
      dialogLabel: 'إعدادات ملفات تعريف الارتباط',
      primaryLabel: 'حسنًا',
      primaryAria: 'الإقرار بإشعار التحليلات',
      secondaryLabel: 'إيقاف التحليلات',
      secondaryAria: 'إيقاف ملفات تعريف الارتباط التحليلية',
    },
  },
}

export default function CookieConsent({
  locale = 'en',
}: {
  locale?: ConsentLocale
}) {
  const [showBanner, setShowBanner] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [policy, setPolicy] = useState<RegionPolicy>('prior-consent')

  useEffect(() => {
    setPolicy(getRegionPolicy())

    // Check if user has already made a decision
    const hasDecision = hasConsentDecision()

    let showTimer: ReturnType<typeof setTimeout> | undefined
    if (!hasDecision) {
      // Short delay only to avoid hydration/layout jank — long enough for
      // paint, short enough that bounce traffic still sees the banner.
      showTimer = setTimeout(() => {
        setShowBanner(true)
        recordConsentMetric('banner_shown')
        // Trigger fade-in animation
        setTimeout(() => setIsVisible(true), 50)
      }, 200)
    }

    // Reopen on request (footer "Cookie Settings" link) even after a decision
    const handleReopen = () => {
      setShowBanner(true)
      setTimeout(() => setIsVisible(true), 50)
    }
    window.addEventListener('pm-consent-reopen', handleReopen)
    return () => {
      if (showTimer) clearTimeout(showTimer)
      window.removeEventListener('pm-consent-reopen', handleReopen)
    }
  }, [])

  // Announce visibility so bottom-anchored overlays (AskShell) can lift
  // themselves above the banner while it is on screen.
  useEffect(() => {
    window.dispatchEvent(new Event('pm-consent-banner-change'))
    return () => {
      // Covers unmount (showBanner -> false) as well
      window.dispatchEvent(new Event('pm-consent-banner-change'))
    }
  }, [isVisible, showBanner])

  const handleAccept = () => {
    // A later change via "Cookie Settings" is a preference change, not a
    // first decision — read before the new record is written.
    const hadDecision = hasConsentDecision()
    // grantConsent writes the v2 record + pm_consent_v2 mirror. It never
    // throws (storage failures fall back to in-memory state), so the banner
    // always completes and analytics always initializes.
    grantConsent(policy === 'notice-opt-out' ? 'notice-ack' : 'banner-accept')
    recordConsentMetric(hadDecision ? 'preference_changed' : 'accepted')
    try {
      window.dispatchEvent(new Event('pm-consent-accepted'))
      // Redundant but harmless: ensure GA flips if GTM shim is present
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        })
      }
    } catch {}
    setIsVisible(false)
    setTimeout(() => setShowBanner(false), 300)
  }

  const handleDecline = () => {
    const hadDecision = hasConsentDecision()
    // denyConsent writes the v2 record + pm_consent_v2 mirror (never throws;
    // in-memory fallback keeps the denial effective for this session).
    denyConsent(
      policy === 'notice-opt-out' ? 'notice-opt-out' : 'banner-decline'
    )
    recordConsentMetric(
      hadDecision
        ? 'preference_changed'
        : policy === 'notice-opt-out'
          ? 'opted_out'
          : 'rejected'
    )
    try {
      window.dispatchEvent(new Event('pm-consent-declined'))
      // Reinforce Consent Mode hard deny for GTM/GA4
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        })
      }
    } catch {}
    setIsVisible(false)
    setTimeout(() => setShowBanner(false), 300)
  }

  if (!showBanner) return null

  const copy = BANNER_COPY[locale][policy]

  return (
    <div
      data-consent-banner
      data-visible={isVisible ? 'true' : 'false'}
      lang={locale}
      dir={locale === 'ar' ? 'rtl' : undefined}
      className={`fixed bottom-0 left-0 right-0 z-[1000] transition-all duration-300 ease-in-out motion-reduce:transition-none lg:bottom-6 lg:left-auto lg:right-6 lg:w-[400px] ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
      role='dialog'
      aria-live='polite'
      aria-label={copy.dialogLabel}
    >
      {/* Phones and tablets: a full-width dock with a 1px top edge. Desktop:
          a compact bottom-right card, so it never covers the hero CTA. */}
      <div aria-hidden='true' className='h-px w-full bg-line lg:hidden' />
      <div className='bg-card pb-[env(safe-area-inset-bottom)] lg:rounded-card lg:border lg:border-line lg:pb-0 lg:shadow-[0_24px_60px_-30px_rgba(18,18,18,0.45)]'>
        <div className='container py-4 lg:px-5 lg:py-5'>
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6 lg:flex-col lg:items-stretch lg:gap-4'>
            {/* Message */}
            <div className='flex-1'>
              <p className='text-sm text-muted-foreground leading-relaxed'>
                <span className='font-semibold text-foreground'>
                  {copy.intro}
                </span>{' '}
                {copy.message}{' '}
                <a
                  href='/privacy-policy'
                  hrefLang='en'
                  /* inline-block + py-1 lifts the hit area to ~24px. As a bare
                     inline link it was ~16px: line-height creates no hit area
                     on an inline non-replaced element, only the font content
                     area does.
                     Not a WCAG failure — SC 2.5.8 exempts links inline in a
                     sentence — but this one sits in a dismissible banner the
                     visitor wants gone, where a 16px target is the difference
                     between reading the policy and mis-tapping Accept. */
                  className='inline-block py-1 text-primary-ink hover:text-foreground underline underline-offset-2 transition-colors duration-200 motion-reduce:transition-none'
                >
                  {copy.learnMore}
                </a>
              </p>
            </div>

            {/* Actions - outlined secondary, filled primary; equal size and reach */}
            <div className='flex gap-2.5 shrink-0 sm:gap-3'>
              <button
                onClick={handleDecline}
                className='inline-flex h-11 flex-1 items-center justify-center rounded-pill border border-line-strong bg-transparent px-5 text-sm font-medium text-foreground transition-colors duration-200 hover:bg-foreground hover:text-background focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cta-ink motion-reduce:transition-none sm:flex-none lg:flex-1'
                aria-label={copy.secondaryAria}
              >
                {copy.secondaryLabel}
              </button>
              <button
                onClick={handleAccept}
                className='inline-flex h-11 flex-1 items-center justify-center rounded-pill bg-cta-fill px-5 text-sm font-medium text-white transition-colors duration-200 hover:bg-cta-fill-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cta-ink motion-reduce:transition-none sm:flex-none lg:flex-1'
                aria-label={copy.primaryAria}
              >
                {copy.primaryLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
